import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

const TYPEN = ['Unfall', 'Schaden', 'Reparatur', 'Wartung', 'Inspektion', 'Pannenhilfe', 'Diebstahl', 'Vandalismus', 'Sonstiges'];

function formatDate(d) {
  if (!d) return '–';
  const [y, m, day] = d.split('-');
  return `${day}.${m}.${y}`;
}

function formatEuro(n) {
  if (!n && n !== 0) return '–';
  return Number(n).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

const EMPTY = { datum: new Date().toISOString().slice(0, 10), typ: '', beschreibung: '', kosten: '', status: 'offen' };

function EreignisForm({ initial, onSave, onClose }) {
  const [form, setForm] = useState(initial ? { ...initial, kosten: initial.kosten ?? '' } : { ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.datum || !form.typ) { setError('Datum und Typ sind erforderlich.'); return; }
    setSaving(true);
    try {
      await onSave(form);
    } catch (err) {
      setError(err.message || 'Fehler beim Speichern.');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{initial ? 'Ereignis bearbeiten' : 'Neues Ereignis'}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Datum *</label>
                <input className="form-input" type="date" value={form.datum} onChange={e => set('datum', e.target.value)} autoFocus />
              </div>
              <div className="form-group">
                <label className="form-label">Typ *</label>
                <select className="form-select" value={form.typ} onChange={e => set('typ', e.target.value)}>
                  <option value="">– Bitte wählen –</option>
                  {TYPEN.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Kosten (€)</label>
                <input className="form-input" type="number" min="0" step="0.01" value={form.kosten} onChange={e => set('kosten', e.target.value)} placeholder="0,00" />
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
                  <option value="offen">Offen</option>
                  <option value="erledigt">Erledigt</option>
                </select>
              </div>
              <div className="form-group form-full">
                <label className="form-label">Beschreibung</label>
                <textarea className="form-textarea" rows={4} value={form.beschreibung || ''} onChange={e => set('beschreibung', e.target.value)} placeholder="Details zum Ereignis…" />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Speichern…' : 'Speichern'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function EreignisseSection({ fahrzeugId }) {
  const [ereignisse, setEreignisse] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [filter, setFilter] = useState('alle'); // 'alle' | 'offen' | 'erledigt'

  const load = useCallback(() => {
    setLoading(true);
    api.getEreignisse(fahrzeugId).then(setEreignisse).catch(console.error).finally(() => setLoading(false));
  }, [fahrzeugId]);

  useEffect(load, [load]);

  const handleSave = async (data) => {
    if (editItem) {
      await api.updateEreignis(editItem.id, data);
    } else {
      await api.createEreignis(fahrzeugId, data);
    }
    setShowForm(false);
    setEditItem(null);
    load();
  };

  const handleDelete = async () => {
    await api.deleteEreignis(deleteId);
    setDeleteId(null);
    load();
  };

  const toggleStatus = async (e) => {
    const newStatus = e.status === 'erledigt' ? 'offen' : 'erledigt';
    await api.updateEreignis(e.id, { ...e, status: newStatus });
    load();
  };

  const filtered = filter === 'alle' ? ereignisse : ereignisse.filter(e => e.status === filter);
  const gesamtkosten = filtered.reduce((s, e) => s + (e.kosten || 0), 0);
  const offenAnzahl = ereignisse.filter(e => e.status === 'offen').length;

  const typIcon = (typ) => {
    const icons = { Unfall: '💥', Schaden: '🔧', Reparatur: '🔩', Wartung: '⚙️', Inspektion: '🔍', Pannenhilfe: '🚨', Diebstahl: '🚔', Vandalismus: '⚠️', Sonstiges: '📋' };
    return icons[typ] || '📋';
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--gray-600)' }}>Fahrzeughistorie</span>
          {offenAnzahl > 0 && <span className="badge badge-warning">{offenAnzahl} offen</span>}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ display: 'flex', border: '1px solid var(--gray-200)', borderRadius: 6, overflow: 'hidden' }}>
            {['alle', 'offen', 'erledigt'].map(f => (
              <button key={f} onClick={() => setFilter(f)} style={{
                padding: '4px 10px', fontSize: 12, fontWeight: 500, border: 'none', cursor: 'pointer',
                background: filter === f ? 'var(--primary)' : '#fff',
                color: filter === f ? '#fff' : 'var(--gray-600)',
                textTransform: 'capitalize'
              }}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => { setEditItem(null); setShowForm(true); }}>
            + Ereignis eintragen
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 20, color: 'var(--gray-400)', fontSize: 13 }}>Lade…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: '28px 20px', color: 'var(--gray-400)', fontSize: 13, textAlign: 'center' }}>
          {filter === 'alle' ? 'Noch keine Ereignisse erfasst.' : `Keine ${filter}en Ereignisse.`}
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Datum</th>
                  <th style={thStyle}>Typ</th>
                  <th style={thStyle}>Beschreibung</th>
                  <th style={thStyle}>Kosten</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(e => (
                  <tr key={e.id} style={e.status === 'offen' ? { background: 'var(--warning-light)' } : {}}>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{formatDate(e.datum)}</td>
                    <td style={tdStyle}>
                      <span style={{ marginRight: 6 }}>{typIcon(e.typ)}</span>
                      <strong>{e.typ}</strong>
                    </td>
                    <td style={{ ...tdStyle, maxWidth: 300 }}>
                      <div style={{ whiteSpace: 'pre-wrap', color: 'var(--gray-600)', fontSize: 13 }}>
                        {e.beschreibung || <span style={{ color: 'var(--gray-300)', fontStyle: 'italic' }}>–</span>}
                      </div>
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                      {e.kosten ? <strong style={{ color: 'var(--gray-900)' }}>{formatEuro(e.kosten)}</strong> : '–'}
                    </td>
                    <td style={tdStyle}>
                      <button
                        onClick={() => toggleStatus(e)}
                        className={`badge ${e.status === 'erledigt' ? 'badge-success' : 'badge-warning'}`}
                        style={{ border: 'none', cursor: 'pointer', padding: '3px 10px' }}
                        title="Klicken zum Umschalten"
                      >
                        {e.status === 'erledigt' ? '✓ Erledigt' : '⏳ Offen'}
                      </button>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => { setEditItem(e); setShowForm(true); }}>✏️</button>
                        <button className="btn btn-danger btn-sm" onClick={() => setDeleteId(e.id)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.some(e => e.kosten) && (
            <div style={{ padding: '10px 20px', borderTop: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'flex-end', gap: 6, fontSize: 13, color: 'var(--gray-600)' }}>
              <span>Gesamtkosten ({filter}):</span>
              <strong style={{ color: 'var(--gray-900)' }}>{formatEuro(gesamtkosten)}</strong>
            </div>
          )}
        </>
      )}

      {showForm && (
        <EreignisForm
          initial={editItem}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditItem(null); }}
        />
      )}

      {deleteId && (
        <div className="modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Ereignis löschen</span>
              <button className="modal-close" onClick={() => setDeleteId(null)}>✕</button>
            </div>
            <div className="modal-body confirm-dialog">
              <p>Dieses Ereignis wirklich löschen?</p>
              <div className="confirm-actions">
                <button className="btn btn-secondary" onClick={() => setDeleteId(null)}>Abbrechen</button>
                <button className="btn btn-danger" onClick={handleDelete}>Löschen</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  textAlign: 'left', padding: '8px 14px',
  fontSize: 11, fontWeight: 600, color: 'var(--gray-500)',
  textTransform: 'uppercase', letterSpacing: '.5px',
  background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)'
};
const tdStyle = { padding: '10px 14px', borderBottom: '1px solid var(--gray-100)', color: 'var(--gray-700)', fontSize: 14, verticalAlign: 'top' };
